import React, { useState, useEffect } from 'react';
import { deleteCompany, getAllCompaniesByIssuer, updateCompany } from '../../../services/apiservice';
import { useAuth } from '../../../contexts/authContext';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import CompanyEditModal from './CompanyEditModal';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

function CompanyList() {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { user } = useAuth();
  const issuerId = user.IssuerID;
  const { data: fetchedCompanies, loading } = useApiData(() => getAllCompaniesByIssuer(issuerId).then((res) => res.data), []);

  useEffect(() => {
    if (fetchedCompanies) {
      setCompanies(fetchedCompanies);
    }
  }, [fetchedCompanies]);

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleDelete = async (company) => {
    const CompanyID = company.CompanyID;
    try {
      setLoadingCompanies(prevState => ({ ...prevState, [CompanyID]: true }));
      const response = await deleteCompany(CompanyID);
      if (response.success) {
        setCompanies(companies.filter(c => c.CompanyID !== CompanyID));
        setSnackbarMessage('Company Deleted successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.message || 'Failed to delete Company.');
        setSnackbarSeverity('error');
      }
    }
    catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoadingCompanies(prevState => ({ ...prevState, [CompanyID]: false }));
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCompany(null);
  };

  const handleModalSave = async (updatedCompany) => {
    try {
      const response = await updateCompany(updatedCompany);
      if (response.success) {
        setCompanies(prevCompanies =>
          prevCompanies.map(company =>
            company.CompanyID === updatedCompany.CompanyID ? { ...company, ...updatedCompany }
              : company
          )
        );
        setSnackbarMessage('Company updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'CompanyID', label: 'Company ID', width: 50 },
    { key: 'CompanyName', label: 'Company Name', width: 150 },
    { key: 'CompanyType', label: 'Company Type', width: 120 },
    { key: 'CompanyIndustry', label: 'Industry', width: 150 },
    { key: 'CompanyHeadquarters', label: 'Headquarters', width: 150 },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Companies List"
        columns={columns}
        rows={companies}
        loading={loading}
        searchKeys={['CompanyName', 'CompanyType', 'CompanyIndustry']}
        rowKey="CompanyID"
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleting={loadingCompanies}
      />
      <CompanyEditModal
        open={modalOpen}
        onClose={handleModalClose}
        company={selectedCompany}
        onSave={handleModalSave}
      />
    </>
  );
}

export default CompanyList;
